import styled from "styled-components";

import { FONTS, QUERIES, SPACING } from "../../../utils/constants";
import { GoalTone } from "./types";
import { BaseButton } from "../../../components/Button";

const TONE_COLOR: Record<GoalTone, string> = {
  success: "var(--color-status-success)",
  info: "var(--color-status-info)",
  warning: "var(--color-status-warning)",
  error: "var(--color-status-error)",
};

const TONE_BACKGROUND: Record<GoalTone, string> = {
  success: "var(--color-status-success-background-soft)",
  info: "var(--color-status-info-background-soft)",
  warning: "var(--color-status-warning-background-soft)",
  error: "var(--color-status-error-background-soft)",
};

export const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xl}px;
`;

export const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const Title = styled.h3`
  margin: 0;
  font-family: ${FONTS.serif};
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--color-text-default);
`;

// The Settings panel sits beside the nav, so two columns only fit from laptop up.
export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACING.md}px;

  @media ${QUERIES.laptopAndUp} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  min-width: 0;
  padding: ${SPACING.md}px;
  border-radius: ${SPACING.md}px;
  border: 1px solid var(--color-border-light);
  background: var(--color-card-background);
  box-shadow: var(--shadow-small);
`;

export const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const NameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.xs}px;
  min-width: 0;
`;

export const GoalName = styled.h4`
  margin: 0;
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--color-text-default);
  overflow-wrap: anywhere;
`;

export const StatusChip = styled.span<{ $tone: GoalTone }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: ${SPACING.xxs}px ${SPACING.sm}px;
  border-radius: ${SPACING.lg}px;
  font-size: var(--text-xs);
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $tone }) => TONE_COLOR[$tone]};
  background: ${({ $tone }) => TONE_BACKGROUND[$tone]};
`;

export const AmountRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CurrentAmount = styled.span`
  font-family: ${FONTS.serif};
  font-size: var(--text-xxl);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-default);
`;

export const TargetAmount = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-muted);
`;

export const MetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);

  @media ${QUERIES.tabletLargeAndUp} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACING.sm}px;
  }
`;

export const SliderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const LinkActions = styled.div`
  display: flex;
  gap: ${SPACING.sm}px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.lg}px;
  border-radius: ${SPACING.md}px;
  border: 1px dashed var(--color-border-light);
  background: var(--color-card-background);
  text-align: center;
`;

// Icon size comes from the MUI `fontSize` prop (token-safe); the wrapper only tints it.
export const EmptyIcon = styled.div`
  display: inline-flex;
  color: var(--color-text-secondary);
`;

export const EmptyText = styled.p`
  margin: 0;
  max-width: 42ch;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
`;

export const Hint = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
`;

export const TargetsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
`;

export const TargetsCard = styled(Card).attrs({ as: "div" })`
  gap: ${SPACING.md}px;
`;

export const TargetRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;

  @media ${QUERIES.tabletAndUp} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 14rem);
    align-items: start;
    gap: ${SPACING.md}px;
  }
`;

export const TargetLabel = styled.label`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${SPACING.xs}px;
  font-size: var(--text-base);
  font-weight: 500;
  color: var(--color-text-default);
`;

export const Comparator = styled.span`
  font-size: var(--text-sm);
  font-weight: 400;
  color: var(--color-text-muted);
`;

export const TargetInputCell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: ${SPACING.sm}px;
`;

export const UnitSuffix = styled.span`
  padding-top: ${SPACING.sm}px;
  min-width: 3rem;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
`;

export const PastToggle = styled(BaseButton)`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: var(--color-primary);
  }
`;

export const PastList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const PastItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  min-width: 0;
  padding: ${SPACING.md}px;
  border-radius: ${SPACING.md}px;
  border: 1px solid var(--color-border-light);
  background: var(--color-card-background);
`;

export const PastLines = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const PastLine = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACING.xs}px ${SPACING.sm}px;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const PastAmount = styled.span`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-default);
`;
